import { FC, PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { useGetUserProjectsQuery, useReviewUserProjectMutation } from '@teachur/http-client';
import type { UserProject, UserProjectGrade } from '@teachur/http-client';
import { useCurrentUser } from '@hooks/users';

export type ProjectsReviewFilter =
  | ''
  | 'pending'
  | 'inReview'
  | 'late'
  | 'approved'
  | 'notYet'
  | 'claimedByMe';

export type SearchMode = 'student' | 'project' | 'course';

export const SEARCH_MODE_OPTIONS: { label: string; value: SearchMode }[] = [
  { label: 'Student', value: 'student' },
  { label: 'Project', value: 'project' },
  { label: 'Course', value: 'course' },
];

export const PAGE_SIZE = 10;

export interface ReviewProjectData {
  userProject: UserProject;
  grades: UserProjectGrade[];
}

export interface ProjectsReviewContextValue {
  projects: UserProject[];
  selectedProject: UserProject | null;
  searchQuery: string;
  searchMode: SearchMode;
  filter: ProjectsReviewFilter;
  dateFrom: string;
  dateTo: string;
  isLoading: boolean;
  isError: boolean;
  isReviewSubmitting: boolean;
  page: number;
  total: number;
  reviewProject: (projectId: number, data: ReviewProjectData) => void;
  setSearchQuery: (value: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  setFilter: (filter: ProjectsReviewFilter) => void;
  setDateFrom: (date: string) => void;
  setDateTo: (date: string) => void;
  setSelectedProjectId: (id: number | null) => void;
  setPage: (page: number) => void;
}

export const ProjectsReviewContext = createContext<ProjectsReviewContextValue | undefined>(
  undefined
);

const VIRTUAL_STATUSES: ProjectsReviewFilter[] = ['late'];
const REAL_STATUSES: ProjectsReviewFilter[] = ['pending', 'inReview', 'approved', 'notYet'];

export const ProjectsReviewProvider: FC<PropsWithChildren> = ({ children }) => {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [searchQuery, setSearchQueryState] = useState('');
  const [searchMode, setSearchModeState] = useState<SearchMode>('student');
  const [filter, setFilterState] = useState<ProjectsReviewFilter>('');
  const [dateFrom, setDateFromState] = useState('');
  const [dateTo, setDateToState] = useState('');
  const [page, setPage] = useState(1);

  const { data: currentUser } = useCurrentUser();

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | undefined> = {
      page,
      pageSize: PAGE_SIZE,
    };

    if (searchQuery.trim()) {
      params['search'] = searchQuery.trim();
      params['searchMode'] = searchMode;
    }

    if (filter === 'claimedByMe' && currentUser?.id) {
      params['instructorId'] = currentUser.id;
    } else if (filter && VIRTUAL_STATUSES.includes(filter)) {
      params['visualStatus'] = filter;
    } else if (filter && REAL_STATUSES.includes(filter)) {
      params['status'] = filter;
    }

    if (dateFrom) params['dateFrom'] = dateFrom;
    if (dateTo) params['dateTo'] = dateTo;

    return params;
  }, [page, searchQuery, searchMode, filter, dateFrom, dateTo, currentUser?.id]);

  const {
    data: projectsData,
    isLoading,
    isError,
  } = useGetUserProjectsQuery(queryParams, {
    keepPreviousData: true,
    staleTime: Infinity,
  });

  const projects = projectsData?.data ?? [];
  const total = projectsData?.total ?? 0;

  const { mutate: reviewUserProject, isPending: isReviewSubmitting } =
    useReviewUserProjectMutation();

  const reviewProject = (projectId: number, data: ReviewProjectData) => {
    reviewUserProject({
      id: projectId,
      userProject: data.userProject,
      grades: data.grades,
    });
  };

  const setSearchQuery = (value: string) => {
    setSearchQueryState(value);
    setPage(1);
  };

  const setSearchMode = (mode: SearchMode) => {
    setSearchModeState(mode);
    setPage(1);
  };

  const setFilter = (newFilter: ProjectsReviewFilter) => {
    setFilterState(newFilter);
    setPage(1);
  };

  const setDateFrom = (date: string) => {
    setDateFromState(date);
    setPage(1);
  };

  const setDateTo = (date: string) => {
    setDateToState(date);
    setPage(1);
  };

  const selectedProject = useMemo(() => {
    if (!selectedProjectId) return null;

    return projects.find((project) => project.id === selectedProjectId) ?? null;
  }, [projects, selectedProjectId]);

  const value: ProjectsReviewContextValue = {
    projects,
    selectedProject,
    searchQuery,
    searchMode,
    filter,
    dateFrom,
    dateTo,
    isLoading,
    isError,
    isReviewSubmitting,
    page,
    total,
    reviewProject,
    setSearchQuery,
    setSearchMode,
    setFilter,
    setDateFrom,
    setDateTo,
    setSelectedProjectId,
    setPage,
  };

  return <ProjectsReviewContext.Provider value={value}>{children}</ProjectsReviewContext.Provider>;
};

export const useProjectsReview = () => {
  const context = useContext(ProjectsReviewContext);

  if (!context) {
    throw new Error('useProjectsReview must be used within ProjectsReviewProvider');
  }

  return context;
};
